import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { ServiceServicePorts } from "../../model";

import { URLS } from "./consts";
import { Create, Delete, Get, GetAll, GetPaginated, Update } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { Request, Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends ServiceServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create({
    languageCode,
    ...params
  }: ServiceServicePorts.ProviderIos.Create.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    ServiceServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.create,
            {
              ...params,
            },
            {
              headers: {
                ...Request.addAcceptLanguageHeader(languageCode),
              },
            },
          ),
        Create.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 201) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override update({
    languageCode,
    ...params
  }: ServiceServicePorts.ProviderIos.Update.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    ServiceServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(
            URLS.update(params.id),
            {
              ...params,
            },
            {
              headers: {
                ...Request.addAcceptLanguageHeader(languageCode),
              },
            },
          ),
        Update.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: ServiceServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 404 && response.body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override get({
    id,
    languageCode,
  }: ServiceServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    ServiceServicePorts.ProviderIos.Get.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.get(URLS.get(id), {
            headers: {
              ...Request.addAcceptLanguageHeader(languageCode),
            },
          }),
        Get.zRes,
      ),

      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(response.body.data));

        if (status === 404 && response.body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getAll(): taskEither.TaskEither<
    UnexpectedError,
    ServiceServicePorts.ProviderIos.GetAll.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getAll), GetAll.zRes),
      taskEither.flatMap(({ body }) => {
        return taskEither.right(body.data.map(mapDtoToEntity));
      }),
    );
  }

  override getPaginated(
    params: ServiceServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    ServiceServicePorts.ProviderIos.GetPaginated.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap(({ body }) => {
        const {
          data: { items, totalCount },
        } = body;
        const {
          success,
          data: pageMeta,
          error,
        } = Pagination.Meta.zSchema.safeParse({
          options: params.pagination,
          totalItems: totalCount,
        });

        if (!success) return taskEither.left(new UnexpectedError(error));

        return taskEither.right({
          items: items.map(mapDtoToEntity),
          meta: pageMeta,
        } satisfies ServiceServicePorts.ProviderIos.GetPaginated.Out);
      }),
    );
  }
}

export { ApiProvider };
